import styled from 'styled-components'
import { useContext, useEffect, useState } from 'react'
import postVideo, { processVideo } from '../libs/video-service'
import postAssessment from '../libs/assessment-service'
import getStudents, { Student } from '../libs/student-service'
import { Class } from '../libs/login-service'
import StudentTable from '../components/StudentTable'
import { Batch } from '../libs/batch-service'
import { ClassContext } from '../App'
import { NoButtonModal, SingleButtonModal } from '../components/shared/Modal'
import { ConfirmSubmitModal } from '../components/assessment/ConfirmSubmitModal'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import SetUpClasses from '../components/assessment/SetUpClasses'
import AssessmentContent from '../components/assessment/AssessmentContent'

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    background-color: #ffffff;
`

const Loading = styled(AiOutlineLoading3Quarters)`
    animation: spin 1s linear infinite;
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`
interface Prop {
    setTitle: React.Dispatch<React.SetStateAction<string>>
}

const getDate = () => {
    var tody = new Date()
    var year = tody.getFullYear()
    var month = tody.getMonth() + 1
    var day = tody.getDate()
    var hour = tody.getHours()
    var min = tody.getMinutes()
    var second = tody.getSeconds()

    return `${year}_${month}_${day}_${hour}_${min}_${second}`
}

const Header = styled.div`
    width: 100%;
    height: 80px;
    display: flex;
    align-items: center;
    padding: 0 30px;
    border-bottom: 1px solid #e5e6eb;
`

export default function Assessment({ setTitle }: Prop) {
    useEffect(() => {
        setTitle('Movement Skill Assessment')
    }, [])
    const classes = useContext(ClassContext)

    const [skill, setSkill] = useState<string>('')
    const [selectedClass, setSelectedClass] = useState<Class>(classes[0])
    const [batch, setBatch] = useState<Batch | undefined>(
        selectedClass?.batches[0]
    )
    const [mediaBlobUrl, setMedisBlobUrl] = useState<string | null>()

    const [student, setStudent] = useState<Student | null>(null)
    const [status, setStatus] = useState<
        'start' | 'confirm' | 'uploading' | 'completed' | 'error' | 'default'
    >('default')

    const [students, setStudents] = useState<Student[]>([])

    useEffect(() => {
        const fetchStudents = async () => {
            if (!selectedClass || !batch) {
                return
            }

            const { students } = await getStudents(selectedClass.id, batch.id)
            setStudents(students)

            if (students.length === 0) {
                setStudent(null)
            }

            setStudent(students[0])
            console.log('students', students)
        }
        fetchStudents()
    }, [selectedClass, batch])

    useEffect(() => {
        setSelectedClass(classes[0])
        setBatch(classes[0]?.batches[0])
    }, [classes])

    const handleClassChange = (classId: number) => {
        const newClass = classes.find((c) => c.id === classId)
        if (newClass) {
            setSelectedClass(newClass)
            setBatch(newClass.batches[0])
        }
    }

    const handleBatchChange = (batchId: number) => {
        const newBatch = selectedClass?.batches.find((b) => b.id === batchId)
        if (newBatch) {
            setBatch(newBatch)
        }
    }
    function handleSubmitClick(
        mediaBlobUrl: string | null,
        selectedSkill?: string
    ) {
        if (!mediaBlobUrl || !student || !batch) {
            return
        }

        if (selectedSkill) {
            setSkill(selectedSkill)
        }

        setMedisBlobUrl(mediaBlobUrl)

        setStatus('start')
    }

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

    const uploadVideo = async () => {
        if (!mediaBlobUrl || !student || !batch) {
            return
        }
        setStatus('uploading')

        try {
            var body = new FormData()
            const video = await fetch(mediaBlobUrl).then((r) => r.blob())
            const videoName = `${student.id}_${skill}_${getDate()}.mp4`
            const file = new File([video], videoName, {
                type: 'video/mp4',
            })
            body.append('file', file)

            const { videoPath } = await postVideo(body)

            const assessment_id = await postAssessment(
                skill,
                student.id,
                batch.id,
                videoPath
            )

            setStatus('completed')
            await sleep(1000)

            setStatus('default')
            await processVideo(assessment_id)
        } catch (e) {
            setStatus('error')
        }
    }

    function handleBadgeClick(student: Student, skill: string) {
        setStudent(student)
        setSkill(skill)
    }

    function handleModalCancel() {
        setStatus('default')
    }

    if (classes.length === 0 || students.length === 0) {
        return <SetUpClasses setTitle={setTitle} />
    }

    return (
        <>
            <Wrapper>
                {status === 'start' && (
                    <ConfirmSubmitModal
                        batch={batch}
                        skill={skill}
                        student={student}
                        handleCancel={handleModalCancel}
                        handleConfirm={uploadVideo}
                    />
                )}
                {status === 'uploading' && (
                    <NoButtonModal
                        children={
                            <>
                                <Loading />
                                'Uploading...'
                            </>
                        }
                    />
                )}
                {status === 'completed' && (
                    <NoButtonModal children={'Upload success!'} />
                )}
                {status === 'error' && (
                    <SingleButtonModal
                        children={
                            'Upload error! Please try again. If still fail, please contract MNG team.'
                        }
                        onCancel={() => {
                            setStatus('default')
                        }}
                    />
                )}

                <AssessmentContent
                    students={students}
                    classes={classes}
                    selectedClass={selectedClass}
                    batch={batch}
                    onClassChange={handleClassChange}
                    onBatchChange={handleBatchChange}
                    status={status}
                    handleSubmitClick={handleSubmitClick}
                    badgeSelectedSkill={skill}
                    selectedStudent={student}
                    onStudentChange={setStudent}
                />
            </Wrapper>
            <Wrapper>
                <Header>
                    Assessment Overview | Track students’ performance in details
                    here
                </Header>

                <StudentTable
                    selectedClass={selectedClass}
                    batch={batch}
                    setStudent={setStudent}
                    badgeClick={handleBadgeClick}
                />
            </Wrapper>
        </>
    )
}
